import React from "react";
import {
  formatDateIntoDMYToDisplay,
  gethoursAndMins,
  getPlanManagedBy,
  textTruncate,
} from "Utils/utils";
import { formatCaseNoteSupportItemFullLabel } from "Utils/caseNoteSupportItem";
import TableHeader from "components/common/Table/TableHeader";
import { DELIVERED_SERVICE_STATUS_OPTIONS } from "Constants/constant";
import useCareLabels from "hooks/useCareLabels";
import CaseNoteHistoryNoteCell from "components/ServiceProvider/Participant/CaseNotes/CaseNoteHistoryNoteCell";

const H = ({
  label,
  minClass = "min-w-[88px]",
  align = "text-left",
  extra = "",
}) => (
  <TableHeader
    isSort={false}
    label={label}
    title={label}
    innerCssClass='block max-w-full truncate'
    cssClass={`bg-blue-50 ${minClass} ${align} overflow-hidden px-2 py-2 text-xs font-semibold text-gray-900 sm:text-sm ${extra}`}
  />
);

const ReportingTable = (props) => {
  const { participantNameLabel: participantNameLabelProp, caseNoteData } = props;
  const { participantNameLabel: participantNameLabelHook } = useCareLabels();
  const participantNameLabel = participantNameLabelProp ?? participantNameLabelHook;

  return (
    <div className='relative mt-4 overflow-x-auto rounded-lg bg-white shadow-md ring-1 ring-black ring-opacity-5'>
      <table className='w-full min-w-[1400px] table-auto border-collapse divide-y divide-gray-300 bg-white'>
        <thead className='bg-red-50 capitalize'>
          <tr>
            <H label={participantNameLabel} minClass='min-w-[9rem]' extra='pl-4' />
            <H label='Funding ID' minClass='min-w-[5.5rem]' />
            <H label='Delivery Serial No.' minClass='min-w-[7rem]' />
            <H label='Support Item' minClass='min-w-[10rem]' />
            <H label='Created By' minClass='min-w-[6rem]' />
            <H label='Created Date' minClass='min-w-[6.75rem]' />
            <H label='Mgmt Style' minClass='min-w-[6rem]' />
            <H label='Delivered Date' minClass='min-w-[6.75rem]' />
            <H label='Hours' minClass='min-w-[5rem]' />
            <H label='Bill Type' minClass='min-w-[7.5rem]' extra='whitespace-nowrap' />
            <H label='Km' minClass='min-w-[3rem]' />
            <H label='Rate' minClass='min-w-[4.5rem]' />
            <H
              label='Delivered Amount'
              minClass='min-w-[7.5rem]'
            />
            <H label='Status' minClass='min-w-[7rem]' />
            <H label='Notes' minClass='min-w-[11rem]' />
            <H
              label='Attachment'
              minClass='min-w-[4.5rem]'
              align='text-center'
            />
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200 bg-white'>
          {caseNoteData &&
            caseNoteData.map((data, index) => {
              const supportDisplay =
                data?.support_item_label ??
                formatCaseNoteSupportItemFullLabel(
                  data?.support_item_number,
                  data?.support_item_name,
                  data?.claim_type
                );
              return (
              <tr key={index} className='even:bg-white odd:bg-gray-50'>
                <td className='max-w-[9rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 capitalize sm:text-sm'>
                  {`${data.participant?.first_name ?? ""} ${data.participant?.last_name ?? ""}`.trim() ||
                    "—"}
                </td>
                <td className='max-w-[5.5rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {data.funding_contract_id ?? "—"}
                </td>
                <td className='max-w-[7rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {data.delivered_service_number}
                </td>
                <td
                  title={supportDisplay}
                  className='max-w-[10rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'
                >
                  {textTruncate(supportDisplay, 40, 39)}
                </td>
                <td
                  title={data?.created_by}
                  className='max-w-[6rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 capitalize sm:text-sm'
                >
                  {textTruncate(data?.created_by, 22, 21)}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {formatDateIntoDMYToDisplay(data?.created_at)}
                </td>
                <td className='max-w-[6rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {getPlanManagedBy(data?.manged_by)}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {formatDateIntoDMYToDisplay(data?.delivered_date)}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {gethoursAndMins(data?.hours, data?.mints)}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs sm:text-sm'>
                  {(data?.bill_types === "Billable" ||
                  data?.bill_types === "billable_hours" ||
                  data?.bill_types === "true") ? (
                    <span className='inline-flex rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800'>
                      Billable
                    </span>
                  ) : (
                    <span className='inline-flex rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700'>
                      Non-Billable
                    </span>
                  )}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {data?.km || 0}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {`$${data?.original_rate ?? ""}`}
                </td>
                <td className='whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {`$${data?.total_amount ?? ""}`}
                </td>
                <td className='max-w-[7rem] overflow-hidden text-ellipsis whitespace-nowrap px-2 py-2 text-start text-xs text-gray-500 sm:text-sm'>
                  {(data?.status === null
                    ? DELIVERED_SERVICE_STATUS_OPTIONS.find((arr) => {
                        return data?.invoice_status === arr.value;
                      })
                    : DELIVERED_SERVICE_STATUS_OPTIONS.find((arr) => {
                        return data?.status === arr.value;
                      })
                  )?.label ?? "—"}
                </td>
                <td className='min-w-[11rem] max-w-[14rem] px-2 py-2 align-top text-left text-xs text-gray-700 sm:text-sm'>
                  <CaseNoteHistoryNoteCell row={data} caseNoteApiModule='ah' />
                </td>
                <td className='whitespace-nowrap px-1 py-2 text-center align-middle'>
                  {(data?.attachments?.length > 0
                    ? data.attachments.filter((item) => item?.attachment_url)
                    : data?.attachment_url
                    ? [
                        {
                          attachment_url: data.attachment_url,
                          original_name: data.file_name,
                        },
                      ]
                    : []
                  ).length > 0 ? (
                    <div className='flex flex-col items-center gap-1'>
                      {(data?.attachments?.length > 0
                        ? data.attachments.filter((item) => item?.attachment_url)
                        : [
                            {
                              attachment_url: data.attachment_url,
                              original_name: data.file_name,
                            },
                          ]
                      ).map((item, attachmentIndex) => (
                        <a
                          key={item.id ?? attachmentIndex}
                          href={item.attachment_url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center justify-center rounded border border-blue-600 bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-800 shadow-sm hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 sm:text-xs'
                          aria-label={
                            item.original_name
                              ? `View attachment ${item.original_name}`
                              : `View attachment ${attachmentIndex + 1}`
                          }
                        >
                          View
                          {(data?.attachments?.length ?? 0) > 1
                            ? ` ${attachmentIndex + 1}`
                            : ""}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className='text-[10px] text-gray-400 sm:text-xs'>—</span>
                  )}
                </td>
              </tr>
              );
            })}
          {!caseNoteData || caseNoteData.length === 0 ? (
            <tr>
              <td colSpan={16} className='py-6 text-center text-red-600'>
                <div>No data found !</div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};
export default ReportingTable;
